import { LocationLevel, QueryLevel } from '../../graphQL/types/shared';
import {
  DataSource,
  YearType,
} from '../../Utils';
import {
  getDataStatusSelector as getCCPY_CP_Selector,
} from '../fetchedData/countryCountryProductYearByCountryProduct';
import {
  getDataStatusSelector as getCCY_C_Selector,
} from '../fetchedData/countryCountryYearByCountry';
import {
  getDataSelector as getCountryLevelSelector,
} from '../fetchedData/countryLevelMapping';
import {
  getDataSelector as getCountryMetadataSelector,
} from '../fetchedData/countryMetadata';
import {
  getDataStatusSelector as getCPY_P_Selector,
} from '../fetchedData/countryProductYearByProduct';

/* Location aggregation groups-related fetches */
import {
  getDataSelector as getGroupsMetadataSelector
} from '../../sharedData/groupsMetadata';
import {
  getDataSelector as getGroupMembersMetadataSelector
} from '../../sharedData/groupMembersMetadata';

import {
  IRegistryEntry,
} from '../getWorkerEnhancer';
import {
  IWorkerRootState,
} from '../workerRootState';
import compute, {
  IComputationInput,
} from './compute';
import {
  checkForInvalidRoutingInput,
  IInputFromURLRouting,
  ISuccessfulMergeAction,
  IUIState,
  START_SUBSCRIBING,
  STOP_SUBSCRIBING,
} from './getReducer';
import {
  getInputFromURLRouting,
  getUIState,
  hasMergeAlreadyBeenComputed,
} from './reducer';

type IRelevantStorePart = {
  countryCountryProductYearByCountryProduct: IWorkerRootState['countryCountryProductYearByCountryProduct'];
  countryCountryYearByCountry: IWorkerRootState['countryCountryYearByCountry'];
  countryLevelMapping: IWorkerRootState['countryLevelMapping'];
  countryProductYearByProduct: IWorkerRootState['countryProductYearByProduct'];
  countryMetadata: IWorkerRootState['countryMetadata'];
} & IWorkerRootState['geo']['uiState'] & IInputFromURLRouting;

const getRelevantSubStore = (rootState: IWorkerRootState) => {
  const {
    countryProductYearByProduct,
    countryCountryYearByCountry,
    countryCountryProductYearByCountryProduct,
    countryLevelMapping, countryMetadata,
    groupsMetadata, groupMembersMetadata,
    geo: {uiState},
  } = rootState;
  const relevantPart: IRelevantStorePart = {
    countryProductYearByProduct,
    countryCountryYearByCountry,
    countryCountryProductYearByCountryProduct,
    countryLevelMapping, countryMetadata,
    groupsMetadata, groupMembersMetadata,
    ...uiState,
    ...getInputFromURLRouting(rootState),
  };
  return relevantPart;
};

/* Start of getComputationInput */

const getData_CPY_P = getCPY_P_Selector();
const getData_CCPY_CP = getCCPY_CP_Selector();
const getData_CCY_C = getCCY_C_Selector();
const getCountryLevelMapping = getCountryLevelSelector();
const getCountryMetadata = getCountryMetadataSelector();
const getGroupsMetadata = getGroupsMetadataSelector();
const getGroupMembersMetadata = getGroupMembersMetadataSelector();

const getComputationInput = (rootState: IWorkerRootState): IComputationInput => {
  const retrieved = getInputFromURLRouting(rootState);
  const {
    year, country, queryLevel, product, productClass, tradeDirection, tradeFlow,
  } = retrieved;
  const {
    width, height,
  } = getUIState(rootState);
  const yearRequest = {
    type: YearType.Single as YearType.Single,
    year,
  };
  const metadata = getCountryMetadata(rootState, {productClass});
  const levelMapping = getCountryLevelMapping(rootState, {});

  const groupsMetadata = getGroupsMetadata(rootState, {});
  const groupMembersMetadata = getGroupMembersMetadata(rootState, {});

  let usePrimaryCountry = country;
  // if(queryLevel === QueryLevel.Group) {
  //   usePrimaryCountry = undefined;
  // }
  let result: IComputationInput;
  if (country !== undefined && product === undefined) {
    const payload = {country, queryLevel, year: yearRequest};
    result = {
      source: DataSource.CCY,
      mainData: getData_CCY_C(rootState, payload),
      primaryCountry: usePrimaryCountry, queryLevel,
      tradeDirection, tradeFlow, metadata, levelMapping, year,
      width, height,
      groupsMetadata, groupMembersMetadata
    };
  } else if (country === undefined && product !== undefined) {
    const payload = {productClass, product, year: yearRequest};
    result = {
      source: DataSource.CPY,
      mainData: getData_CPY_P(rootState, payload),
      primaryCountry: undefined, queryLevel,
      tradeDirection, tradeFlow, metadata, levelMapping, year,
      width, height,
      groupsMetadata, groupMembersMetadata
    };
  } else if (country !== undefined && product !== undefined) {
    const payload = {country, queryLevel, product, productClass, year: yearRequest};

    result = {
      source: DataSource.CCPY,
      mainData: getData_CCPY_CP(rootState, payload),
      primaryCountry: usePrimaryCountry, queryLevel,
      tradeDirection, tradeFlow, metadata, levelMapping, year,
      width, height,
      groupsMetadata, groupMembersMetadata
    };
  } else {
      throw new Error('This code path should not happen');
  }
  return result;
};

const isURLRoutingInputValidForUI = (input: IInputFromURLRouting) => checkForInvalidRoutingInput(input).isValid;

const productSpaceRegistry: IRegistryEntry<
  IWorkerRootState,

  typeof START_SUBSCRIBING,
  typeof STOP_SUBSCRIBING,

  IInputFromURLRouting,
  IUIState,

  IRelevantStorePart,
  IComputationInput,
  ISuccessfulMergeAction
> = {
  startListeningActionName: START_SUBSCRIBING,
  stopListeningActionName: STOP_SUBSCRIBING,
  getRelevantSubStore,
  getComputationInput,
  isURLRoutingInputValidForUI,
  getURLRoutingInputFromRootState: getInputFromURLRouting,
  getUIStateFromRootState: getUIState,
  compute,
  hasMergeAlreadyBeenComputed,
};

export default productSpaceRegistry;
